import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Input } from 'ncoded-component-library';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import DraggableCard from 'components/DraggableCard';
import ResolvePopup from 'components/ResolvePopup';
import SearchIcon from 'icons/Search.icon';
import DragIcon from 'icons/Drag.icon';
import { getArrayLength, getHasElementsInArray } from 'utils';
import {
  computeHideAllColumns,
  filterColumnsBySearch,
  getIsLastVisibleColumn,
  getIsShortenedCounterForm,
  toggleColumnVisibility,
} from 'components/ReorderResizeTable/utils';
import { EMPTY_ARRAY } from 'data/global';

import './ManageColumnsList.styles.scss';

const DRAG_START_DISTANCE = 6;

type ManageColumnsListProps<T extends string> = {
  className?: string;
  allColumns: T[];
  hiddenColumns: string[];
  headingTranslations: Partial<Record<T, string>>;
  lockedColumn?: T;
  onHiddenColumnsChange: (hiddenColumns: string[]) => void;
  onReorderAll: (newFullOrder: T[]) => void;
};

const ManageColumnsList = <T extends string>(
  props: ManageColumnsListProps<T>,
) => {
  const {
    className,
    allColumns,
    hiddenColumns,
    headingTranslations,
    lockedColumn,
    onHiddenColumnsChange,
    onReorderAll,
  } = props;

  const { t } = useTranslation();

  const [searchValue, setSearchValue] = useState('');

  const classes = classNames('manage-columns-list', className);

  const options = useMemo(
    () =>
      allColumns.map((col) => ({
        value: col,
        label: t(headingTranslations[col] || col),
      })),
    [allColumns, headingTranslations, t],
  );

  const filteredOptions = useMemo(
    () => filterColumnsBySearch(options, searchValue),
    [options, searchValue],
  );

  const totalCount = getArrayLength(allColumns);
  const isLongList = getIsShortenedCounterForm(totalCount);
  const isDragDisabled = !!searchValue;
  const visibleCount = totalCount - getArrayLength(hiddenColumns);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: DRAG_START_DISTANCE },
    }),
  );

  const onToggleVisibility = useCallback(
    (col: T) =>
      onHiddenColumnsChange(
        toggleColumnVisibility(hiddenColumns, col, allColumns, lockedColumn),
      ),
    [allColumns, hiddenColumns, lockedColumn, onHiddenColumnsChange],
  );

  const onHideAll = useCallback(
    () =>
      onHiddenColumnsChange(computeHideAllColumns(allColumns, lockedColumn)),
    [allColumns, lockedColumn, onHiddenColumnsChange],
  );

  const onShowAll = useCallback(
    () => onHiddenColumnsChange(EMPTY_ARRAY),
    [onHiddenColumnsChange],
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) return;

      const oldIndex = allColumns.findIndex((col) => col === active.id);
      const newIndex = allColumns.findIndex((col) => col === over.id);

      onReorderAll(arrayMove(allColumns, oldIndex, newIndex));
    },
    [allColumns, onReorderAll],
  );

  const counter = t(
    isLongList ? 'columnsShownCounterCompact' : 'columnsShownCounter',
    { visible: visibleCount, total: totalCount, count: totalCount },
  );

  return (
    <div className={classes}>
      {isLongList ? (
        <>
          <div className="manage-columns-list__search">
            <Input
              prefixNode={<SearchIcon />}
              placeholder={t('searchColumns')}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>

          <div className="manage-columns-list__list-actions">
            <Button variant="link" onClick={onShowAll}>
              {t('showAll')}
            </Button>
            <Button variant="link" onClick={onHideAll}>
              {t('hideAll')}
            </Button>
            <span className="manage-columns-list__counter--short">
              {counter}
            </span>
          </div>
        </>
      ) : (
        <p className="manage-columns-list__counter">{counter}</p>
      )}

      {!getHasElementsInArray(filteredOptions) ? (
        <p className="manage-columns-list__no-matches">
          {t('noMatchesFound')}
        </p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
          autoScroll={false}
        >
          <SortableContext
            items={filteredOptions.map(({ value }) => value)}
            strategy={verticalListSortingStrategy}
          >
            <ul className="manage-columns-list__list">
              {filteredOptions.map(({ value: col, label }) => {
                const isLastVisible = getIsLastVisibleColumn(
                  col,
                  hiddenColumns,
                  allColumns,
                );
                const isLocked = col === lockedColumn;

                const isHidden = hiddenColumns.includes(col);
                const isRowDragDisabled =
                  isDragDisabled || isHidden || isLastVisible || isLocked;

                const checkbox = (
                  <Checkbox
                    label={label}
                    checked={!isHidden}
                    disabled={isLastVisible || isLocked}
                    onChange={() => onToggleVisibility(col as T)}
                  />
                );

                return (
                  <DraggableCard
                    key={col}
                    id={col}
                    disabled={isRowDragDisabled}
                  >
                    {({ attributes, listeners, setActivatorNodeRef }) => (
                      <li className="manage-columns-list__row">
                        <Button
                          variant="icon"
                          icon={DragIcon}
                          className="manage-columns-list__drag-handle"
                          ref={setActivatorNodeRef}
                          disabled={isRowDragDisabled}
                          {...attributes}
                          {...(!isRowDragDisabled && listeners)}
                        />

                        {isLocked ? (
                          <>
                            {checkbox}
                            <span className="manage-columns-list__locked">
                              {t('locked')}
                            </span>
                          </>
                        ) : isLastVisible ? (
                          <ResolvePopup
                            onHover
                            popupContent={t('atLeastOneColumnMustStayVisible')}
                          >
                            {checkbox}
                          </ResolvePopup>
                        ) : (
                          checkbox
                        )}
                      </li>
                    )}
                  </DraggableCard>
                );
              })}
            </ul>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

export default ManageColumnsList;
